import { useEffect, useMemo, useState } from "react";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { InfoScreenCrossfade } from "@/pages/info-screen/components/InfoScreenCrossfade";
import type { InfoScreenEvent } from "@/types/infoScreenEvents";

type Props = {
  cityLabel: string;
  events: InfoScreenEvent[];
  rotationSeconds: number;
};

function formatEventDate(iso: string): string {
  return formatIsoDateShort(iso.slice(0, 10));
}

function EventCard({ event }: { event: InfoScreenEvent }) {
  return (
    <article className="info-screen-event-card">
      {event.image_url ? (
        <div className="info-screen-event-card__image">
          <img src={event.image_url} alt="" loading="lazy" />
        </div>
      ) : (
        <div className="info-screen-event-card__image info-screen-event-card__image--placeholder" />
      )}
      <div className="info-screen-event-card__body">
        {event.category ? (
          <span className="info-screen-event-card__category">{event.category}</span>
        ) : null}
        <h3 className="info-screen-event-card__title">{event.title}</h3>
        <p className="info-screen-event-card__meta">
          <span className="info-screen-event-card__meta-date">
            {formatEventDate(event.starts_at)}
          </span>
          {event.time_label ? (
            <>
              <span className="info-screen-event-card__meta-divider" aria-hidden>
                ·
              </span>
              <span>{event.time_label}</span>
            </>
          ) : null}
        </p>
        {(event.venue_name || event.city) && (
          <p className="info-screen-event-card__venue">
            {[event.venue_name, event.city].filter(Boolean).join(", ")}
          </p>
        )}
        {event.description ? (
          <p className="info-screen-event-card__desc">{event.description}</p>
        ) : null}
      </div>
    </article>
  );
}

export function InfoScreenEventsModule({ cityLabel, events, rotationSeconds }: Props) {
  const [index, setIndex] = useState(0);
  const rotationMs = Math.max(5, rotationSeconds) * 1000;

  const visibleEvents = useMemo(() => events.slice(0, 6), [events]);

  useEffect(() => {
    setIndex(0);
  }, [events]);

  useEffect(() => {
    if (visibleEvents.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % visibleEvents.length);
    }, rotationMs);
    return () => window.clearInterval(timer);
  }, [visibleEvents.length, rotationMs]);

  const active = visibleEvents[index] ?? visibleEvents[0];

  return (
    <div className="info-screen-events-module">
      <header className="info-screen-events-module__header">
        <p className="info-screen-events-module__eyebrow">Heute &amp; demnächst</p>
        <h2 className="info-screen-events-module__title">in {cityLabel}</h2>
      </header>

      {visibleEvents.length === 0 ? (
        <div className="info-screen-events-module__fallback">
          <p className="info-screen-events-module__fallback-title">
            Aktuell keine Veranstaltungen
          </p>
          <p className="info-screen-events-module__fallback-hint">
            Schauen Sie später wieder vorbei – wir halten die Übersicht für Sie aktuell.
          </p>
        </div>
      ) : active ? (
        <InfoScreenCrossfade
          itemKey={active.id}
          variant="slide-up"
          className="info-screen-events-module__carousel"
        >
          <EventCard event={active} />
          {visibleEvents.length > 1 ? (
            <div className="info-screen-events-module__dots" aria-hidden>
              {visibleEvents.map((ev, idx) => (
                <span
                  key={ev.id}
                  className={
                    idx === index
                      ? "info-screen-page-dot info-screen-page-dot--active"
                      : "info-screen-page-dot"
                  }
                />
              ))}
            </div>
          ) : null}
        </InfoScreenCrossfade>
      ) : null}
    </div>
  );
}
